import {
  Combobox,
  Field,
  Heading,
  HStack,
  IconButton,
  Portal,
  Table,
  Text,
  useFilter,
  useListCollection,
  VStack,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  InputField,
  SectionCard,
} from '@themes/aegix';
import { useTranslation } from 'react-i18next';
import { HiPlus, HiTrash } from 'react-icons/hi2';
import type {
  AvailableRole,
  AvailableSchool,
  RoleMapping,
  SchoolMapping,
} from '../../../services';

/**
 * Single-select Combobox with type-to-filter. Mirrors the pattern in
 * libs/themes/aegix/src/components/searchableSelect.tsx (which is
 * multi-select) for the single-value case used here. Each row instance
 * carries its own filter state via `useListCollection`.
 */
interface FilterableSelectProps {
  items: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  triggerId?: string;
  disabled?: boolean;
  emptyMessage?: string;
}

function FilterableSelect({
  items,
  value,
  onChange,
  placeholder,
  triggerId,
  disabled,
  emptyMessage,
}: FilterableSelectProps) {
  const { t } = useTranslation();
  const defaultEmpty = emptyMessage ?? t('common.noMatches', 'No matches');
  const { contains } = useFilter({ sensitivity: 'base' });
  const { collection, filter } = useListCollection({
    initialItems: items,
    filter: contains,
  });

  return (
    <Combobox.Root
      collection={collection}
      value={value ? [value] : []}
      openOnClick
      onValueChange={(e) => onChange(e.value[0] ?? '')}
      onInputValueChange={(e) => filter(e.inputValue)}
      disabled={disabled}
    >
      <Combobox.Control>
        <Combobox.Input id={triggerId} placeholder={placeholder} />
        <Combobox.IndicatorGroup>
          <Combobox.ClearTrigger />
          <Combobox.Trigger />
        </Combobox.IndicatorGroup>
      </Combobox.Control>
      <Portal>
        <Combobox.Positioner zIndex={4300}>
          <Combobox.Content zIndex={4300}>
            <Combobox.Empty>{defaultEmpty}</Combobox.Empty>
            {collection.items.map((item) => (
              <Combobox.Item key={item.value} item={item}>
                {item.label}
                <Combobox.ItemIndicator />
              </Combobox.Item>
            ))}
          </Combobox.Content>
        </Combobox.Positioner>
      </Portal>
    </Combobox.Root>
  );
}

/**
 * Role/School mapping table editor (§9).
 *
 * Shown only when a SCIMConfiguration has no linked SAMLConfig. When a
 * SAMLConfig is linked the parent renders the read-only summary instead.
 */

export interface MappingTableProps {
  roleMappings: RoleMapping[];
  schoolMappings: SchoolMapping[];
  availableRoles: AvailableRole[];
  availableSchools: AvailableSchool[];
  onRoleMappingsChange: (value: RoleMapping[]) => void;
  onSchoolMappingsChange: (value: SchoolMapping[]) => void;
  disabled?: boolean;
  /**
   * Restrict which sub-table renders. Defaults to 'both' (Sites first, then
   * Roles) for back-compat; SamlConfigDrawer splits the two into separate
   * "Sites" and "Roles" sections by passing 'sites' / 'roles'.
   */
  mode?: 'roles' | 'sites' | 'both';
  /** Active claim name shown in the table header (single-mode only). */
  claimName?: string;
  onClaimNameChange?: (value: string) => void;
  /**
   * Optional extra element rendered in the table header between the claim
   * name input and the Add button (used by SamlConfigDrawer to host the
   * shared "Import from CSV" button on the Sites table).
   */
  headerExtra?: React.ReactNode;
}

export function MappingTable({
  roleMappings,
  schoolMappings,
  availableRoles,
  availableSchools,
  onRoleMappingsChange,
  onSchoolMappingsChange,
  disabled,
  mode = 'both',
  claimName,
  onClaimNameChange,
  headerExtra,
}: MappingTableProps) {
  const { t } = useTranslation();

  // Strip the `org_<public_id>_` prefix that the core provisioner stamps
  // onto per-org custom roles
  // (apps/aegix-aim-backend/server/apps/core/provisioning.py:74-87) so the
  // dropdown shows the admin-friendly display name. The full prefixed
  // name stays in `value` so the saved SAMLRoleConfig matches the actual
  // Django Group name and the existing exact-match resolver still works.
  const stripRolePrefix = (name: string): string =>
    name.replace(/^org_[^_]+_/, '');
  const roleItems = availableRoles.map((r) => ({
    label: stripRolePrefix(r.name),
    value: r.name,
  }));
  const schoolItems = availableSchools.map((s) => ({
    label: s.name,
    value: s.name,
  }));

  const addRoleRow = () =>
    onRoleMappingsChange([...roleMappings, { group_name: '', role_name: '' }]);
  const removeRoleRow = (idx: number) =>
    onRoleMappingsChange(roleMappings.filter((_, i) => i !== idx));
  const updateRoleRow = (idx: number, patch: Partial<RoleMapping>) =>
    onRoleMappingsChange(
      roleMappings.map((m, i) => (i === idx ? { ...m, ...patch } : m)),
    );

  const addSchoolRow = () =>
    onSchoolMappingsChange([
      ...schoolMappings,
      { group_name: '', school_name: '' },
    ]);
  const removeSchoolRow = (idx: number) =>
    onSchoolMappingsChange(schoolMappings.filter((_, i) => i !== idx));
  const updateSchoolRow = (idx: number, patch: Partial<SchoolMapping>) =>
    onSchoolMappingsChange(
      schoolMappings.map((m, i) => (i === idx ? { ...m, ...patch } : m)),
    );

  const showSites = mode === 'sites' || mode === 'both';
  const showRoles = mode === 'roles' || mode === 'both';

  // Mirror the aegix SettingsTable column-header styling
  // (apps/aegix-aim-react/src/components/settings/SettingsTable/SettingsTableHeader.tsx)
  // so SAML mapping tables look identical to AIM admin tables.
  const columnHeaderProps = {
    color: 'text.secondary',
    textStyle: 'captionEmphasized' as const,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    _last: { borderRight: 'none' },
    borderColor: 'border.secondary',
    textTransform: 'capitalize' as const,
    textAlign: 'left' as const,
  };

  const bodyRowProps = {
    borderBottom: '1px solid var(--color-border)',
    color: 'text.primary',
    border: 'none',
  } as const;

  const bodyCellProps = {
    color: 'text.primary',
    border: 'none',
  } as const;

  return (
    <VStack align="stretch" gap={8}>
      {showRoles && (
        <SectionCard
          header={
            <>
              <Heading as="h3" textStyle="h2" color="text.primary">
                {t('sso.roles', 'Roles')}
              </Heading>
              <HStack gap={3}>
                {headerExtra}
                <ButtonComponent
                  type="button"
                  visual="secondary"
                  size="sm"
                  onClick={addRoleRow}
                  disabled={disabled}
                  icon={<HiPlus />}
                  data-e2e="sso-add-role-mapping"
                >
                  {t('sso.addRole', 'Add Role')}
                </ButtonComponent>
              </HStack>
            </>
          }
        >
          <Table.Root>
            <Table.Header bg="surface.input">
              <Table.Row
                borderTop="1px solid"
                borderColor="border.secondary"
              >
                <Table.ColumnHeader {...columnHeaderProps}>
                  {t('sso.role', 'Role')}
                </Table.ColumnHeader>
                <Table.ColumnHeader {...columnHeaderProps}>
                  {mode === 'roles' && onClaimNameChange ? (
                    <HStack gap={2} alignItems="center">
                      <Text as="span">{t('sso.claimLabel', 'Claim:')}</Text>
                      <Field.Root flex={1}>
                        <Field.Label htmlFor="role-claim-name" srOnly>
                          Role claim name
                        </Field.Label>
                        <InputField
                          id="role-claim-name"
                          value={claimName ?? ''}
                          onChange={(e) => onClaimNameChange(e.target.value)}
                          placeholder={t('sso.claimNamePlaceholder', 'name')}
                          disabled={disabled}
                          size="sm"
                        />
                      </Field.Root>
                    </HStack>
                  ) : (
                    t('sso.claim', 'Claim')
                  )}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  {...columnHeaderProps}
                  width="56px"
                  aria-label="Row actions"
                />
              </Table.Row>
            </Table.Header>
            <Table.Body color="text.primary" pb="15px">
              {roleMappings.length === 0 ? (
                <Table.Row {...bodyRowProps}>
                  <Table.Cell
                    {...bodyCellProps}
                    colSpan={3}
                    textAlign="center"
                    color="text.secondary"
                    py={8}
                  >
                    {t('sso.noRoleMappingsNotice', 'No role mappings. Click "Add Role" to create one.')}
                  </Table.Cell>
                </Table.Row>
              ) : (
                roleMappings.map((m, idx) => (
                  <Table.Row key={idx} {...bodyRowProps}>
                    <Table.Cell {...bodyCellProps}>
                      <Field.Root>
                        <Field.Label
                          htmlFor={`role-mapping-role-${idx}`}
                          srOnly
                        >
                          {`Role mapping row ${idx + 1} Role`}
                        </Field.Label>
                        <FilterableSelect
                          items={roleItems}
                          value={m.role_name}
                          onChange={(v) =>
                            updateRoleRow(idx, { role_name: v })
                          }
                          placeholder={t('sso.selectRolePlaceholder', 'Select a role')}
                          triggerId={`role-mapping-role-${idx}`}
                          disabled={disabled}
                          emptyMessage={t('sso.noRolesMatch', 'No roles match')}
                          data-e2e="idp-group-name-input"
                        />
                      </Field.Root>
                    </Table.Cell>
                    <Table.Cell {...bodyCellProps}>
                      <Field.Root>
                        <Field.Label
                          htmlFor={`role-mapping-group-${idx}`}
                          srOnly
                        >
                          {`Role mapping row ${idx + 1} IdP group name`}
                        </Field.Label>
                        <InputField
                          id={`role-mapping-group-${idx}`}
                          value={m.group_name}
                          onChange={(e) =>
                            updateRoleRow(idx, { group_name: e.target.value })
                          }
                          placeholder={t('sso.claimValuePlaceholder', 'value')}
                          disabled={disabled}
                        />
                      </Field.Root>
                    </Table.Cell>
                    <Table.Cell {...bodyCellProps} textAlign="center">
                      <IconButton
                        aria-label="Remove role mapping"
                        size="xs"
                        variant="ghost"
                        onClick={() => removeRoleRow(idx)}
                        disabled={disabled}
                      >
                        <HiTrash />
                      </IconButton>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </SectionCard>
      )}

      {showSites && (
        <SectionCard
          header={
            <>
              <Heading as="h3" textStyle="h2" color="text.primary">
                {t('common.sites', 'Sites')}
              </Heading>
              <HStack gap={3}>
                {headerExtra}
                <ButtonComponent
                  type="button"
                  visual="secondary"
                  size="sm"
                  onClick={addSchoolRow}
                  disabled={disabled}
                  icon={<HiPlus />}
                  data-e2e="sso-add-school-mapping"
                >
                  {t('sso.addSite', 'Add Site')}
                </ButtonComponent>
              </HStack>
            </>
          }
        >
          <Table.Root>
            <Table.Header bg="surface.input">
              <Table.Row
                borderTop="1px solid"
                borderColor="border.secondary"
              >
                <Table.ColumnHeader {...columnHeaderProps}>
                  {t('sso.site', 'Site')}
                </Table.ColumnHeader>
                <Table.ColumnHeader {...columnHeaderProps}>
                  {mode === 'sites' && onClaimNameChange ? (
                    <HStack gap={2} alignItems="center">
                      <Text as="span">{t('sso.claimLabel', 'Claim:')}</Text>
                      <Field.Root flex={1}>
                        <Field.Label htmlFor="site-claim-name" srOnly>
                          Site claim name
                        </Field.Label>
                        <InputField
                          id="site-claim-name"
                          value={claimName ?? ''}
                          onChange={(e) => onClaimNameChange(e.target.value)}
                          placeholder={t('sso.claimNamePlaceholder', 'name')}
                          disabled={disabled}
                          size="sm"
                        />
                      </Field.Root>
                    </HStack>
                  ) : (
                    t('sso.claim', 'Claim')
                  )}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  {...columnHeaderProps}
                  width="56px"
                  aria-label="Row actions"
                />
              </Table.Row>
            </Table.Header>
            <Table.Body color="text.primary" pb="15px">
              {schoolMappings.length === 0 ? (
                <Table.Row {...bodyRowProps}>
                  <Table.Cell
                    {...bodyCellProps}
                    colSpan={3}
                    textAlign="center"
                    color="text.secondary"
                    py={8}
                  >
                    {t('sso.noSiteMappingsNotice', 'No site mappings. Click "Add Site" to create one.')}
                  </Table.Cell>
                </Table.Row>
              ) : (
                schoolMappings.map((m, idx) => (
                  <Table.Row key={idx} {...bodyRowProps}>
                    <Table.Cell {...bodyCellProps}>
                      <Field.Root>
                        <Field.Label
                          htmlFor={`school-mapping-site-${idx}`}
                          srOnly
                        >
                          {`Site mapping row ${idx + 1} Site`}
                        </Field.Label>
                        <FilterableSelect
                          items={schoolItems}
                          value={m.school_name}
                          onChange={(v) =>
                            updateSchoolRow(idx, { school_name: v })
                          }
                          placeholder={t('sso.selectSitePlaceholder', 'Select a site')}
                          triggerId={`school-mapping-site-${idx}`}
                          disabled={disabled}
                          emptyMessage={t('sso.noSitesMatch', 'No sites match')}
                          data-e2e="idp-group-name-input"
                        />
                      </Field.Root>
                    </Table.Cell>
                    <Table.Cell {...bodyCellProps}>
                      <Field.Root>
                        <Field.Label
                          htmlFor={`school-mapping-group-${idx}`}
                          srOnly
                        >
                          {`Site mapping row ${idx + 1} IdP group name`}
                        </Field.Label>
                        <InputField
                          id={`school-mapping-group-${idx}`}
                          value={m.group_name}
                          onChange={(e) =>
                            updateSchoolRow(idx, { group_name: e.target.value })
                          }
                          placeholder={t('sso.claimValuePlaceholder', 'value')}
                          disabled={disabled}
                        />
                      </Field.Root>
                    </Table.Cell>
                    <Table.Cell {...bodyCellProps} textAlign="center">
                      <IconButton
                        aria-label="Remove site mapping"
                        size="xs"
                        variant="ghost"
                        onClick={() => removeSchoolRow(idx)}
                        disabled={disabled}
                      >
                        <HiTrash />
                      </IconButton>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </SectionCard>
      )}
    </VStack>
  );
}
